import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getInsightsRecomputeStatus,
  startInsightsRecompute,
} from '../services/catalog-operations.service';
import {
  clearPendingRecompute,
  readPendingRecompute,
  savePendingRecompute,
} from '../utils/recompute-persistence';

/** Poll cadence for a running recompute. The workflow settles in ~1–2s in
 *  practice, so 2s lands within a tick of completion without hammering. */
export const RECOMPUTE_POLL_MS = 2000;

/** Ceiling on how long a single recompute may stay `RUNNING` before the UI
 *  stops asking. Generous against a 1–2s workflow — this exists so a wedged
 *  run releases the spinner rather than polling for the rest of the session. */
export const RECOMPUTE_POLL_BUDGET_MS = 60_000;

/**
 * How a recompute ended, from the UI's point of view. `notFound` and `timeout`
 * are kept apart from `FAILED`: neither is a failure verdict on the workflow,
 * and both still warrant re-reading the insights.
 */
export type RecomputeOutcome = 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'notFound' | 'timeout';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Polls one run to a terminal state. `RUNNING` is the only status that
 * continues the loop — collapsing the other three into it is what would make a
 * dead run poll forever. Gives up at the budget rather than spinning on a
 * workflow that never closes.
 */
export async function pollInsightsRecomputeUntilSettled(runId: string): Promise<RecomputeOutcome> {
  const deadline = Date.now() + RECOMPUTE_POLL_BUDGET_MS;
  while (Date.now() < deadline) {
    const run = await getInsightsRecomputeStatus(runId);
    // Null is the absorbed 404 — unknown id, aged-out Temporal retention, or
    // another subsidiary's run. On a recompute we know started it is harmless:
    // the rows are already persisted, so read them instead of reporting it.
    if (run === null) return 'notFound';
    if (run.status !== 'RUNNING') return run.status;
    await sleep(RECOMPUTE_POLL_MS);
  }
  return 'timeout';
}

interface UseInsightsRecomputeResult {
  /** True from the moment the trigger fires until the run settles — spans the POST,
   *  the poll, AND a poll resumed after a page reload, so one flag drives both the
   *  button's spinner and the tab's "recomputing" state. */
  isRecomputing: boolean;
  /** Fires the recompute. Caller is expected to have confirmed first. */
  start: () => void;
}

/** Surfaces a settled recompute's non-success outcomes as a toast. Kept apart so
 *  the live run and the reload-resume path report failures identically. */
function toastRecomputeFailure(outcome: RecomputeOutcome): void {
  if (outcome === 'FAILED' || outcome === 'CANCELLED') {
    toast.error(
      outcome === 'CANCELLED'
        ? 'The recompute was cancelled. Performance signals are unchanged.'
        : 'The recompute failed. Performance signals are unchanged.',
    );
  }
}

/** `isResuming` → re-polling a run left in-flight by a reload. Tracked per
 *  workflow so a batch switch resets it cleanly. */
interface ResumeState {
  id: string;
  isResuming: boolean;
}

/** Seeds resume state during render (not in an effect) by checking for a persisted
 *  runId, so the loading state is already up on the first paint after a reload. */
function initialResumeState(workflowId: string): ResumeState {
  return {
    id: workflowId,
    isResuming: workflowId ? readPendingRecompute('insights', workflowId) !== null : false,
  };
}

/**
 * Runs one Performance Signals recompute end to end: POST, poll the Temporal
 * status until it settles, then refresh the insights the tab renders. The live
 * run is a mutation (a one-shot imperative action); a run orphaned by a page
 * reload is picked back up by a render-reconciled resume effect. `isRecomputing`
 * spans both, so one flag drives the button and the tab's "recomputing" state.
 */
export function useInsightsRecompute(
  workflowId: string,
  connectorCode: string,
): UseInsightsRecomputeResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (): Promise<RecomputeOutcome> => {
      const run = await startInsightsRecompute(workflowId, connectorCode);
      // Persist before polling so a reload mid-run can pick the poll back up.
      savePendingRecompute('insights', workflowId, run.runId);
      const outcome = await pollInsightsRecomputeUntilSettled(run.runId);
      clearPendingRecompute('insights', workflowId);
      return outcome;
    },
    onSuccess: (outcome) => {
      if (outcome === 'FAILED' || outcome === 'CANCELLED') {
        toastRecomputeFailure(outcome);
        return;
      }
      if (outcome === 'timeout') {
        // Not a verdict — the workflow may still land. Say so, and refresh
        // below in case it already has.
        toast.warning(
          'The recompute is taking longer than expected. Refresh shortly to see results.',
        );
      } else {
        // `notFound` gets the weaker claim: the rows are there to read, but the
        // 404 means we never actually observed the run complete.
        toast.success(
          outcome === 'COMPLETED'
            ? 'Performance signals recomputed.'
            : 'Performance signals refreshed.',
        );
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batchInsights(workflowId),
      });
    },
    onError: (error) => {
      // The POST or a non-404 poll error threw — drop the marker so a reload
      // doesn't keep resuming a run we can't observe.
      clearPendingRecompute('insights', workflowId);
      toast.error(extractApiError(error, 'Could not start the recompute. Please try again.'));
    },
  });

  // Detect a reload-orphaned run at render; reconcile on a batch switch (the
  // codebase's "adjust state during render" pattern, avoiding a setState-in-effect).
  const [resume, setResume] = useState<ResumeState>(() => initialResumeState(workflowId));
  if (resume.id !== workflowId) setResume(initialResumeState(workflowId));
  const activeResume = resume.id === workflowId ? resume : initialResumeState(workflowId);

  // Resume a recompute that was still running when the page was reloaded: re-poll the
  // persisted runId to completion, then refresh and settle, keeping the loading state
  // up across the reload. Failures surface; success is silent (the rows just refresh)
  // to avoid a spurious toast from a stale marker. Stale results are ignored by the
  // `s.id === workflowId` identity guard rather than a cancelled flag.
  useEffect(() => {
    if (!activeResume.isResuming) return;
    void (async () => {
      const runId = readPendingRecompute('insights', workflowId);
      let outcome: RecomputeOutcome = 'notFound';
      if (runId) {
        try {
          outcome = await pollInsightsRecomputeUntilSettled(runId);
        } catch {
          // A non-404 status error — stop resuming and drop the marker.
          outcome = 'notFound';
        }
        clearPendingRecompute('insights', workflowId);
      }
      toastRecomputeFailure(outcome);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batchInsights(workflowId),
      });
      setResume((s) => (s.id === workflowId ? { ...s, isResuming: false } : s));
    })();
  }, [activeResume.isResuming, workflowId, queryClient]);

  return {
    isRecomputing: mutation.isPending || activeResume.isResuming,
    start: () => {
      mutation.mutate();
    },
  };
}
